import React, { useCallback, useMemo } from 'react';
import { useObservable } from 'react-use-observable';

import { Button, Card, Table, Tag, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import PermissionHide from 'v2/components/Shared/PermissionHide';
import Truncate from 'v2/components/Shared/TruncateText';
import { dateMomentFormat } from 'v2/formatters/date';
import { ETicketStatus } from 'v2/interfaces/help';
import type { ITicket } from 'v2/interfaces/help';
import helpService from 'v2/services/help';

import { Icon } from '@eduzz/ui-antd-theme';

import { NEW_TICKET_URL } from '../Actions/content';

interface MyTicketsProps {
  handleHistoryClick: (ticketId: number) => void;
}

const STATUS_TAG: Record<number, { label: string; color: string }> = {
  [ETicketStatus.Novo]: { label: 'Novo', color: 'processing' },
  [ETicketStatus.Atribuído]: { label: 'Atribuído', color: 'default' },
  [ETicketStatus.Planejado]: { label: 'Planejado', color: 'default' },
  [ETicketStatus.Pendente]: { label: 'Pendente', color: 'warning' },
  [ETicketStatus.Solucionado]: { label: 'Solucionado', color: 'success' },
  [ETicketStatus.Fechado]: { label: 'Fechado', color: 'default' }
};

const MyTickets = ({ handleHistoryClick }: MyTicketsProps) => {
  useObservable(() => helpService.loadTickets(), []);
  const [isLoading] = useObservable(() => helpService.getLoadingTickets(), []);
  const [data] = useObservable(() => helpService.getTickets(), []);

  const handleTicketHistoryClick = useCallback(
    (tickedId: number) => handleHistoryClick(tickedId),
    [handleHistoryClick]
  );

  const columns: ColumnsType<ITicket> = useMemo(
    () => [
      {
        title: 'Ticket',
        dataIndex: 'id',
        key: 'id',
        width: 100
      },
      {
        title: 'Título',
        dataIndex: 'title',
        key: 'title',
        render: (title: string) => (
          <Truncate count={50} final='...'>
            {title}
          </Truncate>
        )
      },
      {
        title: 'Criação',
        dataIndex: 'created_at',
        key: 'created_at',
        width: 160,
        render: (value: string) => (
          <Typography.Text type='secondary'>{dateMomentFormat(value, 'DD/MM/YYYY HH:mm')}</Typography.Text>
        )
      },
      {
        title: 'Última atualização',
        dataIndex: 'updated_at',
        key: 'updated_at',
        width: 180,
        render: (value: string) => (
          <Typography.Text type='secondary'>{dateMomentFormat(value, 'DD/MM/YYYY HH:mm')}</Typography.Text>
        )
      },
      {
        title: 'Data de solução',
        dataIndex: 'solved_at',
        key: 'solved_at',
        width: 160,
        render: (value: string | null) => (
          <Typography.Text type='secondary'>
            {value ? dateMomentFormat(value, 'DD/MM/YYYY HH:mm') : '-'}
          </Typography.Text>
        )
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 140,
        render: (status: number) => {
          const config = STATUS_TAG[status] ?? { label: String(status), color: 'default' };
          return <Tag color={config.color}>{config.label}</Tag>;
        }
      },
      {
        title: '',
        key: 'actions',
        width: 60,
        fixed: 'right',
        align: 'center',
        render: (_: unknown, ticket) => (
          <Tooltip title='Visualizar histórico' placement='top'>
            <Button
              shape='circle'
              type='text'
              aria-label='Visualizar histórico'
              onClick={() => handleTicketHistoryClick(ticket.id)}
              icon={<Icon.History size={16} />}
            />
          </Tooltip>
        )
      }
    ],
    [handleTicketHistoryClick]
  );

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex flex-wrap items-center justify-between gap-3'>
        <Typography.Title level={4} className='m-0'>
          Meus tickets
        </Typography.Title>

        <PermissionHide action='myeduzz:ajuda:abrir-chamado'>
          <Button type='primary' icon={<Icon.Plus size={16} />} href={NEW_TICKET_URL} target='_blank' rel='noreferrer'>
            Novo Ticket
          </Button>
        </PermissionHide>
      </div>

      <Card>
        <Table<ITicket>
          rowKey='id'
          columns={columns}
          dataSource={data || []}
          loading={!!isLoading}
          scroll={{ x: 900 }}
          pagination={false}
          locale={{ emptyText: 'Nenhum registro encontrado' }}
        />
      </Card>
    </div>
  );
};

export default MyTickets;
